import { DialogCloseEvent, DialogCloseReason } from '@sui/dialog';
import { Observable, firstValueFrom, isObservable } from 'rxjs';

/**
 * Close data an editor dialog may carry: `saved` settles when the request behind an
 * optimistic Save close has finished and rejects when it failed. Every
 * `FormEditorDialogBase` dialog and the export templates editor set it; a dialog that
 * closes only after its save succeeded leaves it out.
 */
export interface SavedCloseData {
  saved?: Promise<unknown>;
}

/**
 * The handle an editor entry point returns (architecture.md, "Row models and editor
 * dialogs"): resolves with the close event once the dialog is gone, or with null when
 * no dialog opened (permission denied, fetch failed, nothing to edit). A caller that
 * does not need the outcome drops it; a caller that owns a row model passes it to
 * `editorSaved` and refreshes the row.
 */
export type EditorClose = Promise<DialogCloseEvent<unknown> | null>;

const SAVE_REASONS: ReadonlySet<DialogCloseReason> = new Set([
  DialogCloseReason.Save,
  DialogCloseReason.Update,
  DialogCloseReason.SaveAndClose,
]);

/** True for the close reasons that mean "the user saved". */
export function isSaveClose(
  event: DialogCloseEvent<unknown> | null | undefined,
): boolean {
  return !!event && SAVE_REASONS.has(event.reason);
}

/**
 * Resolves true once an editor closed with a Save reason and its save settled
 * successfully, false for every other outcome (cancelled, nothing opened, save
 * failed). Accepts an editor handle, a dialog's `closed` stream, or nothing (an
 * opener that returns void).
 */
export async function editorSaved(
  close: EditorClose | Observable<DialogCloseEvent<unknown>> | void,
): Promise<boolean> {
  if (!close) {
    return false;
  }

  const event = isObservable(close)
    ? await firstValueFrom(close, { defaultValue: null })
    : await close;

  if (!isSaveClose(event)) {
    return false;
  }

  const saved = (event.data as SavedCloseData | undefined)?.saved;
  if (!saved) {
    return true;
  }

  try {
    await saved;
    return true;
  } catch {
    return false;
  }
}
